// navContributions — the typed registry the v3 inline nav arrays decompose into (contract §18,
// ratified: each feature exports a typed `nav.ts` contribution beside `routes.ts`; the shell
// composes them here — the 484-line `DefaultLayout.vue` array and the `PanelLayout.vue` admin
// array both die). Features (or the app wiring on their behalf) register per-context item
// providers; `useNavigation()` resolves the active context's items and evaluates them.
//
// Providers are FUNCTIONS so dynamic children (dashboards, compositions, account centers — v3
// `computed` arrays) stay reactive: they are invoked inside `useNavigation`'s `computed`.
import type { NavContext, NavItem } from './types'

export interface NavContribution {
  context: NavContext
  /** Sidebar position — ascending, ties resolved by registration order (sort is stable). */
  order?: number
  items: () => NavItem[]
}

const contributions: NavContribution[] = []

/** Register a nav contribution. Returns its unregister (idempotent). */
export function registerNavContribution(contribution: NavContribution): () => void {
  contributions.push(contribution)
  return () => {
    const index = contributions.indexOf(contribution)
    if (index !== -1) contributions.splice(index, 1)
  }
}

/** The composed, ordered item list for one context — `[]` until features contribute. */
export function resolveNavItems(context: NavContext): NavItem[] {
  return contributions
    .filter((contribution) => contribution.context === context)
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
    .flatMap((contribution) => contribution.items())
}

/** Test isolation only — not used on the hot path. */
export function clearNavContributions(): void {
  contributions.length = 0
}
